/**
 * Die Angaben, die Suchmaschinen und Sprachmodelle auslesen, ohne die Seite zu betrachten.
 *
 * Sie stehen als JSON-LD im ausgelieferten HTML — seit dem Prerendern auch für
 * Crawler, die kein JavaScript ausführen.
 */

import { detailTitel } from './seoTexte';
import { buildLocationPath } from './urls';

export const BASE_URL = 'https://www.dein-imker.de';

/**
 * Die Einträge einer Übersichtsseite in der Reihenfolge, in der sie dastehen.
 *
 * Ohne diese Liste ist eine Ortsseite für eine Suchmaschine eine Seite mit Text
 * darauf; mit ihr ist sie eine Aufzählung von Verkaufsstellen, deren Reihenfolge
 * und Ziel benannt sind.
 */
export const itemListe = (locations = [], name) => {
    const eintraege = locations
        .map((loc, i) => {
            const pfad = buildLocationPath(loc);
            if (!pfad) return null;
            return {
                '@type': 'ListItem',
                position: i + 1,
                name: detailTitel(loc),
                url: `${BASE_URL}${pfad}`
            };
        })
        .filter(Boolean);

    if (eintraege.length === 0) return undefined;

    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name,
        numberOfItems: eintraege.length,
        itemListElement: eintraege
    };
};

/**
 * Der Weg von der Startseite bis hierher.
 *
 * Entsteht aus denselben Angaben wie die sichtbare Navigation. Zwei getrennte
 * Quellen würden früher oder später auseinanderlaufen, und dann behauptet das
 * Schema einen anderen Weg als die Seite zeigt.
 *
 * Der letzte Eintrag hat kein Ziel — er ist die Seite selbst. Schema.org erlaubt
 * das: item darf beim letzten Element fehlen.
 */
export const breadcrumbListe = (items = []) => {
    if (items.length < 2) return undefined;

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, i) => {
            const eintrag = {
                '@type': 'ListItem',
                position: i + 1,
                name: item.label
            };
            if (item.to) {
                eintrag.item = `${BASE_URL}${item.to}`;
            }
            return eintrag;
        })
    };
};

/**
 * Wer hinter dem Verzeichnis steht.
 *
 * sameAs verbindet die Domain mit den übrigen Auftritten desselben Betreibers.
 * Für Suchmaschinen ist das der Beleg, dass hinter dein-imker.de nicht eine
 * beliebige Sammlung von Adressen steht, sondern eine seit Jahren betriebene
 * Anwendung mit Einträgen in beiden App-Stores.
 */
export const organisation = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'dein-Imker.de',
    url: BASE_URL,
    logo: `${BASE_URL}/dein-Imker.svg`,
    sameAs: [
        'https://www.ibeekeeper.de/',
        'https://apps.apple.com/de/app/bienenv%C3%B6lker-deine-imker-app/id1198119768',
        'https://play.google.com/store/apps/details?id=de.iBeekeeper.Beehives'
    ]
});

/**
 * Mehrere Angaben für eine Seite.
 *
 * Google liest mehrere Blöcke je Seite; leere fallen weg, damit kein
 * `null` im Markup landet.
 */
export const zusammen = (...teile) => {
    const vorhanden = teile.filter(Boolean);
    if (vorhanden.length === 0) return undefined;
    if (vorhanden.length === 1) return vorhanden[0];
    return vorhanden;
};
